import { REPORTS_QA } from 'constants/routePaths';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import ViewerQuery from '@graphql/queries/ViewerQuery';
import CodeIcon from '@mui/icons-material/Code';
import { Alert, Box, Button, Snackbar } from '@mui/material';
import SubmitFooter from 'components/ReportRequestV2/SubmitFooter';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import selectGgEnvironment from 'selectors/ggEnvironment';
import selectTokenRoles from 'selectors/sessionTokenRoles';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';
import { hasSuperAdminRole } from 'utils/roleUtils';

import useFormSubmission from './hooks/useFormSubmission';
import useParentDetails from './hooks/useParentDetails';
import useParentReports from './hooks/useParentReports';
import useProductCatalog from './hooks/useProductCatalog';
import useReportRequestState from './hooks/useReportRequestState';
import useTemplatePrefill from './hooks/useTemplatePrefill';
import { REPORT_KIND_LABELS } from './model/constants';
import { getSteps, StepDefinition, StepId } from './model/steps';
import { summarise } from './model/summary';
import { FormMode } from './model/types';
import { validateAllSteps } from './model/validation';
import ConfirmSubmitDialog from './parts/ConfirmSubmitDialog';
import FormHeader from './parts/FormHeader';
import FormStepSection, { sectionElementId } from './parts/FormStepSection';
import IncompleteSections from './parts/IncompleteSections';
import ReportJsonSidebar from './parts/ReportJsonSidebar';
import SubmittedBanner from './parts/SubmittedBanner';
import styles from './ReportRequestV2.module.css';
import StepContent from './steps/StepContent';

const REPORTS_HOME = REPORTS_QA;

interface ViewerData {
  viewer: { id: number; email: string; name: string | null } | null;
}

export interface ReportRequestV2Props {
  mode?: FormMode;
}

const COPY: Record<
  FormMode,
  { title: string; submittedTitle: string; submitLabel: string }
> = {
  request: {
    title: 'Request a report',
    submittedTitle: 'Report requested',
    submitLabel: 'Submit request',
  },
  update: {
    title: 'Update report',
    submittedTitle: 'Update submitted',
    submitLabel: 'Submit update',
  },
};

const subtitleFor = (mode: FormMode, kindLabel: string | null) => {
  if (mode === 'update') return 'Add products to an existing report.';
  return kindLabel ?? 'Choose a report type to see the rest of the form.';
};

const classes = (...names: (string | false)[]) =>
  names.filter(Boolean).join(' ');

interface FooterState {
  resolving: boolean;
  viewerFailed: boolean;
  ready: boolean;
  incomplete: StepDefinition[];
  paramsValid: boolean;
  onReveal: (id: StepId) => void;
}

/** What the footer shows in place of Submit while the form can't be submitted yet. */
const footerStatus = ({
  resolving,
  viewerFailed,
  ready,
  incomplete,
  paramsValid,
  onReveal,
}: FooterState): React.ReactNode => {
  if (resolving) return 'Still loading the template…';
  if (incomplete.length > 0)
    return <IncompleteSections steps={incomplete} onReveal={onReveal} />;
  if (viewerFailed)
    return 'Your account could not be loaded. Refresh the page to try again.';
  // The params need the viewer's name, so they can't validate until it loads.
  if (!ready) return 'Loading…';
  if (!paramsValid)
    return 'The report parameters failed validation. This is a bug in the form, not your input; please report it.';
  return undefined;
};

/**
 * One single-page form for every report request: market survey, optimization
 * and mix & match (`mode="request"`), and product updates (`mode="update"`).
 * Every section for the chosen report type is shown at once, and the answers
 * stay on the page after submitting.
 */
const ReportRequestV2: React.FC<ReportRequestV2Props> = ({
  mode = 'request',
}) => {
  const history = useHistory();
  const environment = useSelector(selectGgEnvironment);
  const isSuperAdmin = hasSuperAdminRole(useSelector(selectTokenRoles));
  const producerId = Number(useSelector(selectWorkspaceProducerId));
  const viewerQuery = useQuery<ViewerData>(ViewerQuery);
  const viewer = viewerQuery.data?.viewer;

  const { state, dispatch, prefill, templateName } =
    useReportRequestState(mode);
  const isRequest = mode === 'request';

  const catalog = useProductCatalog({
    skip: mode !== 'update' && state.reportKind !== 'market_survey',
  });
  const prefillStatus = useTemplatePrefill(prefill, catalog, dispatch);
  const parentReports = useParentReports({
    kind: state.reportKind,
    isSuperAdmin,
    workspaceId: producerId,
  });
  const parentDetails = useParentDetails(state.parent, dispatch);
  const ready = !prefillStatus.resolving && !!viewer;
  const copy = COPY[mode];

  const context = React.useMemo(() => ({ isSuperAdmin }), [isSuperAdmin]);
  const steps = getSteps(state, context);
  const [showJson, setShowJson] = React.useState(false);
  const topRef = React.useRef<HTMLDivElement>(null);

  const stepErrors = React.useMemo(
    () => validateAllSteps(state, context),
    [state, context],
  );
  const summary = React.useMemo(() => summarise(state), [state]);
  const jumpTo = (id: StepId) =>
    document
      .getElementById(sectionElementId(id))
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  const {
    params,
    paramsValid,
    schemaIssues,
    submitting,
    attempted,
    revealErrors,
    confirmOpen,
    cancelConfirm,
    requestSubmit,
    confirm,
    failure,
    clearFailure,
    submission,
    dismissSubmission,
    startNew,
    formVersion,
  } = useFormSubmission({
    mode,
    state,
    dispatch,
    viewer,
    environment,
    ready,
    onSubmitted: () =>
      topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
  });

  const incompleteSteps = steps.filter((s) => stepErrors[s.id]);
  const revealSection = (id: StepId) => {
    revealErrors();
    jumpTo(id);
  };

  // Clone / Update open from a report's menu, so return there when possible.
  const cancel = () => {
    if (history.length > 1) history.goBack();
    else history.push(REPORTS_HOME);
  };

  const kindLabel = state.reportKind
    ? REPORT_KIND_LABELS[state.reportKind]
    : null;
  const hasForm = !!state.reportKind && (isRequest || !!state.updateSource);
  const jsonVisible = isSuperAdmin && showJson && hasForm;
  const sectionErrors = (id: StepId) => (attempted && stepErrors[id]) || {};

  return (
    <Box
      className={classes(styles.page, jsonVisible && styles.pageWide)}
      ref={topRef}
    >
      <FormHeader
        title={copy.title}
        subtitle={subtitleFor(mode, kindLabel)}
        templateName={templateName}
        showTemplateNotice={!!prefill && isRequest}
        resolvingTemplate={prefillStatus.resolving}
        parentMissing={prefillStatus.parentMissing}
        actions={
          isSuperAdmin && hasForm && !showJson ? (
            <Button startIcon={<CodeIcon />} onClick={() => setShowJson(true)}>
              Show report JSON
            </Button>
          ) : undefined
        }
      />

      {submission && (
        <SubmittedBanner
          title={copy.submittedTitle}
          description={`${submission.kindLabel} “${submission.projectName}” is queued. You will get a Slack notification when it finishes. Your answers are kept below.`}
          reportUid={submission.uid}
          onGoToReportsQa={() => history.push(REPORTS_QA)}
          onStartNew={isRequest ? startNew : undefined}
          onClose={dismissSubmission}
        />
      )}

      <Box
        className={classes(
          styles.layout,
          jsonVisible && styles.layoutWithSidebar,
        )}
      >
        <Box className={styles.content} component="main">
          {steps.map((step) => (
            <FormStepSection
              key={`${step.id}-${formVersion}`}
              step={step}
              invalid={Object.keys(sectionErrors(step.id)).length > 0}
            >
              <StepContent
                stepId={step.id}
                state={state}
                dispatch={dispatch}
                errors={sectionErrors(step.id)}
                isSuperAdmin={isSuperAdmin}
                producerId={producerId}
                catalog={catalog}
                missingProducts={prefillStatus.missingProducts}
                parentReports={parentReports}
                parentDetails={parentDetails}
              />
            </FormStepSection>
          ))}
        </Box>

        {jsonVisible && (
          <ReportJsonSidebar
            params={params}
            issues={schemaIssues}
            onHide={() => setShowJson(false)}
          />
        )}
      </Box>

      <SubmitFooter
        onSubmit={requestSubmit}
        onCancel={cancel}
        submitting={submitting}
        showSubmit={ready && incompleteSteps.length === 0 && paramsValid}
        submitLabel={copy.submitLabel}
        status={footerStatus({
          resolving: prefillStatus.resolving,
          viewerFailed: !!viewerQuery.error,
          ready,
          incomplete: incompleteSteps,
          paramsValid,
          onReveal: revealSection,
        })}
      />

      <ConfirmSubmitDialog
        open={confirmOpen}
        title={`Submit this ${kindLabel?.toLowerCase() ?? 'report'}?`}
        projectName={params?.project_name}
        sections={summary}
        onCancel={cancelConfirm}
        onConfirm={confirm}
        submitting={submitting}
      />

      <Snackbar open={!!failure} onClose={clearFailure}>
        <Alert severity="error" onClose={clearFailure} variant="filled">
          Submission failed: {failure}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportRequestV2;
